"use client";

import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import Markdown from "react-markdown";
import Select, { MultiValue } from "react-select";

import FetchError from "@/components/ui/fetch-error";
import Loading from "@/components/ui/loading";
import { OptionProps, PresentationProps } from "@/types";

import PresentationTable from "./presentation-table";

import api from "@/lib/api";

export default function PresentationList() {
  const {
    data: presentations = [],
    error,
    isPending,
  } = useQuery({
    queryKey: ["presentations-hcap"],
    queryFn: () => api.get<PresentationProps[]>("/cms/presentations/hcap/"),
  });

  const [selectedStudies, setSelectedStudies] = useState<OptionProps[]>([]);

  // Split into upcoming and past presentations
  const upcomingPresentations = presentations.filter((pres) => pres.is_upcoming);
  const pastPresentations = presentations.filter((pres) => !pres.is_upcoming);

  // Filter presentations based on selected studies
  const filteredPresentations = useMemo(() => {
    if (selectedStudies.length === 0) return pastPresentations;

    return pastPresentations.filter((presentation) => {
      return presentation.studies.some((studies) =>
        selectedStudies.some((selectedStudy) => selectedStudy.value === studies.id),
      );
    });
  }, [selectedStudies, pastPresentations]);

  const handleChange = (newValue: MultiValue<OptionProps> | null) => {
    setSelectedStudies(newValue ? Array.from(newValue) : []);
  };

  // Get unique studies from all past presentations
  const uniqueStudies = useMemo(() => {
    return Array.from(
      new Set(
        pastPresentations.flatMap((presentation) =>
          presentation.studies.map((study) => JSON.stringify({ value: study.id, label: study.title })),
        ),
      ),
    ).map((study) => JSON.parse(study));
  }, [pastPresentations]);

  if (isPending) return <Loading />;
  if (error) return <FetchError message={error.message} />;

  return (
    <>
      {upcomingPresentations.length > 0 && (
        <section className="mb-5">
          <h4 className="g2-text-navy">Upcoming</h4>
          <hr />
          {upcomingPresentations.map((presentation) => (
            <div key={presentation.id}>
              <h5>{presentation.title}</h5>
              <div>
                <Markdown>{presentation.content}</Markdown>
              </div>
            </div>
          ))}
        </section>
      )}
      <section>
        <h4 className="g2-text-navy">Past Webinars & Presentations</h4>
        <hr />
        <div className="row mb-3">
          <div className="col-md-4">
            <Select
              defaultValue={[]}
              isMulti
              instanceId="studies"
              name="studies"
              placeholder="Filter by study..."
              options={uniqueStudies}
              onChange={handleChange}
              classNamePrefix="select"
            />
          </div>
        </div>
        <PresentationTable presentations={filteredPresentations} />
      </section>
    </>
  );
}
